import {
  Component,
  Input,
  OnDestroy,
  ViewEncapsulation,
} from "@angular/core";
import { FormGroup } from "@angular/forms";

import { MatDialog } from "@angular/material/dialog";

import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loadedTenantState,
  priceSignTypesLoadedState,
  siteGradeListLoadedState,
  sitePriceSignsLoadedState,
} from "src/app/store/reducers";
import {
  getPriceSignTypesList,
  getSiteGradeList,
  getSitePriceSignList,
} from "src/app/store/actions/sites/sites.actions";

import {
  PriceSignType,
  SitePriceSignDTO,
} from "src/app/models/Sites/sitePriceSignDTO";
import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";

import { PriceSignFormDialogComponent } from "./dialog/price-sign-form-dialog.component";

@Component({
  selector: "site-price-signs",
  templateUrl: "./price-signs.component.html",
  styleUrls: ["./price-signs.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PriceSignsComponent implements OnDestroy {
  _siteCode: string;

  @Input() set siteCode(code: string) {
    this._siteCode = code;
    if (this._siteCode) {
      this.loadPriceSigns(this._siteCode);
      this.loadSiteGrades(this._siteCode);
      this.loadPriceSignTypes();
    }
  }

  priceSignList$: Observable<SitePriceSignDTO[]>;
  priceSignList: SitePriceSignDTO[];
  priceSignSub: Subscription;
  currentPriceSign: SitePriceSignDTO;

  //Grades List
  siteGradeList$: Observable<SiteGradeDTO[]>;
  siteGradeList: SiteGradeDTO[];
  siteGradeSub: Subscription;

  //Price Sign Types list
  priceSignTypeList$: Observable<PriceSignType[]>;
  priceSignTypeList: PriceSignType[];
  priceSignTypeSub: Subscription;

  //Coin symbol
  tenantCoinSymbol$: Observable<string>;

  priceSignDataForm: FormGroup;
  isPriceSignSelected = false;
  
  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog
  ) {
    this.priceSignList$ = this.store.pipe(select(sitePriceSignsLoadedState));
    this.siteGradeList$ = this.store.pipe(select(siteGradeListLoadedState));
    this.priceSignTypeList$ = this.store.pipe(select(priceSignTypesLoadedState));
    this.tenantCoinSymbol$ = this.store
      .pipe(select(loadedTenantState))
      .pipe(map(({ currency }) => currency?.symbol));
  }

  loadPriceSigns(code: string) {
    this.priceSignSub = this.priceSignList$.subscribe(
      (priceSigns: SitePriceSignDTO[]) => {
        this.priceSignList = priceSigns;
        if (priceSigns && priceSigns.length && this.currentPriceSign) {
          const { code: currentPriceSignCode } = this.currentPriceSign;
          this.currentPriceSign = priceSigns.find(({ code }) => code === currentPriceSignCode);
        }
        this.resetPriceSign();
      }
    );
    this.store.dispatch(getSitePriceSignList({ 
      code,
      pageReq: { realSize: true, offset: 0, limit: 999 },
    }));
  }

  loadSiteGrades(code: string) {
    this.siteGradeSub = this.siteGradeList$.subscribe(
      (grades: SiteGradeDTO[]) => {
        this.siteGradeList = grades;
      }
    );
    this.store.dispatch(getSiteGradeList({ code }));
  }

  loadPriceSignTypes() {
    this.priceSignTypeSub = this.priceSignTypeList$.subscribe(
      (priceSignTypes: PriceSignType[]) => {
        this.priceSignTypeList = priceSignTypes;
      }
    );
    this.store.dispatch(getPriceSignTypesList(null));
  }

  getChipForeColor(color: string) {
    return getColorByBgColor(`#${color}`);
  }

  resetPriceSign() {
    this.dialog.closeAll();
    this.isPriceSignSelected = false;
    this.currentPriceSign = null;
  }
  
  selectPriceSign(priceSign: SitePriceSignDTO) {
    this.isPriceSignSelected = true;
    this.currentPriceSign = priceSign;
    
    this.openPriceSignFormDialog(false); 
  }

  create() {
    this.isPriceSignSelected = false;
    this.currentPriceSign = null;

    this.openPriceSignFormDialog(true);
  }

  openPriceSignFormDialog(isCreate: boolean) {
    this.dialog.open(PriceSignFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate,
        siteCode: this._siteCode,
        currentPriceSign: this.currentPriceSign,
        priceSignTypeList: this.priceSignTypeList,
        siteGradeList: this.siteGradeList,
      }
    });
  }

  ngOnDestroy(): void {
    if (this.priceSignSub) {
      this.priceSignSub.unsubscribe();
    }
    if (this.siteGradeSub) {
      this.siteGradeSub.unsubscribe();
    }
    if (this.priceSignTypeSub) {
      this.priceSignTypeSub.unsubscribe();
    }
  }
}
